// Cuadro «Ranking por tienda» del mockup, con las SIETE columnas que hoy tienen dato.
//
// La que falta —«Aprob.»— no se pinta: el endpoint no la sirve porque el dato no se puede leer por
// ventana, y una columna de guiones se lee como «esta tienda no aprueba nada». El drill-down a
// vendedores tampoco. El nombre de la tienda es un botón: selecciona su punto en el scatter del C5.
import type { StoreOriginationRow } from '@/api/services/retail-wizard/backoffice';
import { cn } from '@/lib/cn';
import {
    formatPct,
    formatUnits,
    mixSegments,
    sortRanking,
    storeLabel,
    UNKNOWN_FINANCIER_KEY,
    type RankingColumn,
    type SortDirection,
} from '../lib/originationMetrics';
import { COHORT_HEALTHY_PCT } from '../lib/originationCohort';
import { CaptureDeltaChip, DownPaymentDeltaChip } from './OriginationDeltas';

const TH =
    'bg-muted px-2 py-[5px] text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';
const TD = 'border-b border-border/50 px-2 py-[5px] text-[11px]';
const NUM = 'text-right tabular-nums';

/** Los colores de la barra de mezcla: SureKeep en azul y las demás en la familia naranja del mock. */
const MIX_COLOR: Record<string, string> = {
    SUREKEEP: 'hsl(217 91% 55%)',
    PAYJOY: 'hsl(28 86% 60%)',
    CREDIYA: 'hsl(38 86% 62%)',
    LESPAGO: 'hsl(18 80% 62%)',
    INNOVA: 'hsl(48 80% 60%)',
    [UNKNOWN_FINANCIER_KEY]: 'hsl(215 16% 65%)',
};

export const RANKING_COLUMN_LABEL: Record<RankingColumn, string> = {
    storeName: 'Tienda',
    sureKeepUnits: 'Vol SK',
    capturePct: 'Captura',
    downPaymentAvgPct: 'Enganche',
    firstInstallmentCohortPct: '1ª cuota 14d',
    captureChangePp: 'Δ capt.',
};

/**
 * La celda de la cosecha. `null` es **N/D**, no un cero: la tienda no tiene créditos con la
 * antigüedad suficiente. Por debajo del umbral saludable se pinta en rojo, como el mock.
 */
function CohortCell({ row }: { row: StoreOriginationRow }) {
    if (row.firstInstallmentCohortPct === null) {
        return (
            <span
                className="text-muted-foreground"
                title="Ningún crédito de esta tienda cumplió los 14 días todavía: no hay elegibles que dividir"
            >
                N/D
            </span>
        );
    }
    const poor = row.firstInstallmentCohortPct < COHORT_HEALTHY_PCT;
    return (
        <span
            className={cn('whitespace-nowrap', poor && 'font-semibold text-destructive')}
            title={`${formatUnits(row.firstInstallmentEligibleCredits)} créditos elegibles · umbral saludable ≥ ${String(COHORT_HEALTHY_PCT)}%`}
        >
            {formatPct(row.firstInstallmentCohortPct)}
        </span>
    );
}

/** La barra apilada de mezcla por financiera de una tienda (`.mini-mix-bar` del mock). */
function MixBar({ row }: { row: StoreOriginationRow }) {
    const segments = mixSegments(row);
    if (segments.length === 0) {
        return <span className="text-[10px] text-muted-foreground">—</span>;
    }
    const summary = segments
        .map((seg) => `${seg.label}: ${formatUnits(seg.units)} (${seg.sharePct.toFixed(0)}%)`)
        .join(' · ');
    return (
        <span
            className="inline-flex h-3 w-[68px] overflow-hidden rounded-[2px] bg-muted/50 align-middle"
            title={summary}
            role="img"
            aria-label={`Mezcla por financiera — ${summary}`}
        >
            {segments.map((seg) => (
                <span
                    key={seg.key}
                    className="block h-full"
                    style={{
                        width: `${String(seg.sharePct)}%`,
                        backgroundColor: MIX_COLOR[seg.key] ?? 'hsl(215 16% 65%)',
                    }}
                />
            ))}
        </span>
    );
}

/**
 * Qué cobró la tienda contra lo que su plan le exigía. Sin mínimo del catálogo se dice el enganche
 * y nada más: inventar un «mín. 0 %» afirmaría que el plan no pedía enganche.
 */
function downPaymentSummary(row: StoreOriginationRow): string {
    const paid = `Enganche promedio ${formatPct(row.downPaymentAvgPct)}`;
    if (row.minDownPaymentAvgPct === null) {
        return `${paid} · el mínimo de su plan no se pudo leer`;
    }
    return `${paid} · mínimo de su plan ${formatPct(row.minDownPaymentAvgPct)}`;
}

/** Encabezado ordenable, con el mismo caret del resto del portal. */
function SortTh({
    column,
    numeric = false,
    sort,
    onSort,
}: {
    column: RankingColumn;
    numeric?: boolean;
    sort: { column: RankingColumn; direction: SortDirection };
    onSort: (column: RankingColumn) => void;
}) {
    const active = sort.column === column;
    return (
        <th
            scope="col"
            className={cn(TH, numeric && 'text-right')}
            aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <button
                type="button"
                onClick={() => {
                    onSort(column);
                }}
                className="inline-flex items-center gap-1 uppercase tracking-[0.04em] hover:text-foreground"
            >
                {RANKING_COLUMN_LABEL[column]}
                <span
                    aria-hidden
                    className={cn('text-[8px]', active ? 'opacity-100' : 'opacity-0')}
                >
                    {active && sort.direction === 'asc' ? '▲' : '▼'}
                </span>
            </button>
        </th>
    );
}

export function OriginationRanking({
    rows,
    sort,
    onSort,
    selectedStoreId,
    onSelectStore,
}: {
    rows: StoreOriginationRow[];
    sort: { column: RankingColumn; direction: SortDirection };
    onSort: (column: RankingColumn) => void;
    selectedStoreId: string | null;
    onSelectStore: (storeId: string) => void;
}) {
    const sorted = sortRanking(rows, sort.column, sort.direction);
    return (
        <table className="w-full border-collapse text-[11px]">
            <thead className="sticky top-0 z-[1]">
                <tr>
                    <SortTh column="storeName" sort={sort} onSort={onSort} />
                    <SortTh column="sureKeepUnits" numeric sort={sort} onSort={onSort} />
                    <SortTh column="capturePct" numeric sort={sort} onSort={onSort} />
                    {/* El mock llama «Mezcla» a la mezcla por NIVEL DE RIESGO, que todavia no tiene fuente.
                        Esta es por financiera: se nombra distinto para que la columna no afirme lo que no es. */}
                    <th
                        scope="col"
                        className={TH}
                        title="Cómo se repartió el financiamiento de esa tienda entre SureKeep y las demás financieras"
                    >
                        Mezcla financ.
                    </th>
                    <SortTh column="downPaymentAvgPct" numeric sort={sort} onSort={onSort} />
                    <SortTh
                        column="firstInstallmentCohortPct"
                        numeric
                        sort={sort}
                        onSort={onSort}
                    />
                    <SortTh column="captureChangePp" numeric sort={sort} onSort={onSort} />
                </tr>
            </thead>
            <tbody>
                {sorted.map((row) => (
                    <tr
                        key={row.storeId}
                        className={cn(
                            'hover:bg-accent/50',
                            row.storeId === selectedStoreId && 'bg-primary/10 hover:bg-primary/15',
                        )}
                    >
                        <td className={TD}>
                            {/* El botón y no la fila entera: un `onClick` en el `tr` no se alcanza
                                con el teclado, y el nombre es lo que el operador va a apuntar. */}
                            <button
                                type="button"
                                onClick={() => {
                                    onSelectStore(row.storeId);
                                }}
                                aria-pressed={row.storeId === selectedStoreId}
                                title="Resaltar esta tienda en el scatter de «Cómo originamos»"
                                className="text-left font-medium hover:underline"
                            >
                                {storeLabel(row)}
                            </button>
                            {row.zoneName ? (
                                <span className="ml-1.5 text-[9.5px] text-muted-foreground">
                                    {row.zoneName}
                                </span>
                            ) : null}
                        </td>
                        <td className={cn(TD, NUM)}>{formatUnits(row.channels.sureKeep.units)}</td>
                        <td className={cn(TD, NUM)}>
                            {row.capturePct === null ? (
                                <span
                                    className="text-muted-foreground"
                                    title="Esta tienda no vendió nada en la ventana: no hay base para calcular la captura"
                                >
                                    —
                                </span>
                            ) : (
                                formatPct(row.capturePct)
                            )}
                        </td>
                        <td className={TD}>
                            <MixBar row={row} />
                        </td>
                        <td className={cn(TD, NUM)}>
                            {row.downPaymentAvgPct === null ? (
                                <span
                                    className="text-muted-foreground"
                                    title="Ninguna venta de esta tienda registró enganche con precio de equipo"
                                >
                                    —
                                </span>
                            ) : (
                                <span className="whitespace-nowrap" title={downPaymentSummary(row)}>
                                    {formatPct(row.downPaymentAvgPct)}
                                    <DownPaymentDeltaChip overMinPp={row.downPaymentOverMinPp} />
                                </span>
                            )}
                        </td>
                        <td className={cn(TD, NUM)}>
                            <CohortCell row={row} />
                        </td>
                        <td className={cn(TD, NUM)}>
                            <CaptureDeltaChip changePp={row.captureChangePp} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
